import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button/Button';
import { getUser, authAPI } from '../../api/client';
import { useUserSettingsStore } from '../../store/userSettingsStore';
import { useCurrentStep, useEvaluationStore } from '../../store/evaluationStore';
import { MenuIcon, XIcon, EditIcon } from '../Icons/Icons';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import ColorPaletteMenuSelector from '../ColorPaletteSelector/ColorPaletteMenuSelector';
import { Stepper } from '../Stepper/Stepper';
import type { User } from '../../types';
import { useToast } from '../../contexts/ToastContext';

export const MainLayout: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { addToast } = useToast();
  const [forceUpdate, setForceUpdate] = useState(0);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState('');
  const { userSettings } = useUserSettingsStore();
  const currentStep = useCurrentStep();
  const { evaluationTitle, setEvaluationTitle, markAsChanged } = useEvaluationStore();

  useEffect(() => {
    const currentUser = getUser();
    setUser(currentUser);
  }, []);

  // Listen for language changes to force re-render
  useEffect(() => {
    const handleLanguageChange = () => {
      setForceUpdate(prev => prev + 1);
    };
    
    window.addEventListener('languageChanged', handleLanguageChange);
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      setUser(null);
      navigate('/auth');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const isEvaluationPage = location.pathname === '/evaluation' || location.pathname.startsWith('/evaluation/');
  const hasProjectId = location.pathname.match(/\/evaluation\/[^/]+\/([^/]+)/)?.[1];

  const handleStartEditingTitle = () => {
    setTempTitle(evaluationTitle || '');
    setIsEditingTitle(true);
  };

  const handleSaveTitle = () => {
    const newTitle = tempTitle.trim();
    if (newTitle && newTitle !== evaluationTitle) {
      setEvaluationTitle(newTitle);
      markAsChanged();
    }
    setIsEditingTitle(false);
  };

  const handleCancelEditingTitle = () => {
    setIsEditingTitle(false);
    setTempTitle('');
  };

  const handleTitleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      handleCancelEditingTitle();
    }
  };

  return (
    <div className="app">

      {/* ヘッダー */}
      <header className="app-header">
        <div className="app-header__content">
          <h1 className="app-title">
            {isEvaluationPage ? (
              <>
                <span onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>Evaloid</span>
                <span className="title-separator">-</span>
                {isEditingTitle ? (
                  <input
                    type="text"
                    value={tempTitle}
                    onChange={(e) => setTempTitle(e.target.value)}
                    onKeyDown={handleTitleKeyPress}
                    onBlur={handleSaveTitle}
                    className="title-edit-input"
                    autoFocus
                  />
                ) : (
                  <>
                    <span className="evaluation-title">
                      {hasProjectId && !evaluationTitle ? '...' : (evaluationTitle || t('status.untitled'))}
                    </span>
                    <button
                      onClick={handleStartEditingTitle}
                      className="title-edit-button"
                      title={t('common.edit')}
                    >
                      <EditIcon />
                    </button>
                  </>
                )}
                {userSettings?.securityMode && (
                  <span className="security-mode-indicator" title={t('security.modeEnabled')}>
                    🔒
                  </span>
                )}
              </>
            ) : (
              <>
                <span onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>Evaloid</span>
                {userSettings?.securityMode && (
                  <span className="security-mode-indicator" title={t('security.modeEnabled')}>
                    🔒
                  </span>
                )}
              </>
            )}
          </h1>
          
          {/* Stepper - Only show on evaluation page */}
          {isEvaluationPage && (
            <div className="app-header__stepper-inline">
              <Stepper />
            </div>
          )}
          
          {/* Hamburger Menu Button - Right side */}
          <Button
            variant="ghost"
            className="menu-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <XIcon /> : <MenuIcon />}
          </Button>
        </div>
        
        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="mobile-menu">
            <div className="mobile-menu__user">
              <span className="user-info__name">
                {t('msg.welcomeWithName', { name: userSettings?.displayName || user?.fullName })}
              </span>
            </div>
            <nav className="mobile-menu__nav">
              <Button
                variant="ghost"
                onClick={() => {
                  navigate('/');
                  setIsMenuOpen(false);
                }}
                className={isActive('/') ? 'active' : ''}
              >
                {t('nav.dashboard')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  navigate('/billing');
                  setIsMenuOpen(false);
                }}
                className={isActive('/billing') ? 'active' : ''}
              >
                {t('plan.professional')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  navigate('/settings');
                  setIsMenuOpen(false);
                }}
                className={isActive('/settings') ? 'active' : ''}
              >
                {t('nav.settings', { fallback: '設定' })}
              </Button>
              
              {/* Language Switcher in Menu */}
              <div className="mobile-menu__language">
                <span className="mobile-menu__language-label">{t('menu.language', { fallback: '言語設定' })}</span>
                <LanguageSwitcher />
              </div>
              
              {/* Color Palette Selector in Menu */}
              <div className="mobile-menu__color-palette">
                <span className="mobile-menu__color-palette-label">{t('menu.colorPalette', { fallback: 'カラーパレット' })}</span>
                <ColorPaletteMenuSelector />
              </div>
              
              <Button variant="secondary" onClick={handleLogout}>
                {t('auth.logout', { fallback: 'ログアウト' })}
              </Button>
            </nav>
          </div>
        )}
        
        {/* Overlay when menu is open */}
        {isMenuOpen && (
          <div 
            className="mobile-menu-overlay" 
            onClick={() => setIsMenuOpen(false)}
          />
        )}
        
      </header>

      {/* アプリコンテンツ */}
      <main className="app-content">
        <Outlet />
      </main>

      <footer className="app-footer">
        <div className="app-footer__container">
          <div className="app-footer__content">
            <div className="app-footer__legal">
              <a href="/privacy-policy" className="app-footer__link">{t('footer.privacyPolicy')}</a>
              <span className="app-footer__separator">|</span>
              <a href="/terms-of-service" className="app-footer__link">{t('footer.termsOfService')}</a>
            </div>
            <p className="app-footer__text">
              © 2024 Jool. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};